import * as Types from '../gql/_types.js';

import { BlackoutModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = BlackoutModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetBlackoutsQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.blackouts.errors = [];
    state.blackouts.loading = true;
    try {
        let { limit, offset, orderBy } = state.blackouts.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetBlackouts({...originalPayload, ...finalPayload})
        state.blackouts.meta.total = meta.aggregate?.count || 0;
        state.blackouts.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
    }
    state.blackouts.loading = false;
}

export const resetState: Action = ({state}) => {
    state.blackouts.errors = []
    state.blackouts.loading = false
    state.blackouts.items = {}
    state.blackouts.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.blackouts.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.blackouts.resetMeta()
    actions.blackouts.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.blackouts.errors = [];
    state.blackouts.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.blackouts.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetBlackouts({...originalPayload, ...finalPayload})
        state.blackouts.meta.total = meta.aggregate?.count || 0;
        state.blackouts.meta.page = newPage;
        state.blackouts.meta.offset = newOffset;
        state.blackouts.items = { ...state.blackouts.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
    }
    state.blackouts.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetBlackoutsQueryVariables> = async ({state, effects}, payload) => {
    state.blackouts.errors = [];
    state.blackouts.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetBlackouts(payload)
        state.blackouts.meta.total = meta.aggregate?.count || 0;
        state.blackouts.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
    }
    state.blackouts.loading = false;
}

export const getItem: AsyncAction<Types.GetBlackoutQueryVariables,Types.GetBlackoutQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetBlackout(payload);
        return item;
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetBlackoutQueryVariables> = async ({state, actions}, payload) => {
    state.blackouts.loading = true;
    try {
        let item = await actions.blackouts.getItem(payload);
        if (item) {
            state.blackouts.currentItemId = item.id;
            state.blackouts.items[item.id] = item;
        }
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
    }
    state.blackouts.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.blackouts.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.blackouts.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.BlackoutFragment>> = async ({state}, meta) => {
    state.blackouts.meta = {
        ...state.blackouts.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateBlackoutMutationVariables['object'],Types.CreateBlackoutMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateBlackoutMutationVariables['object']>(payload);
    state.blackouts.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateBlackout({object:finalPayload});
        return item;
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateBlackoutMutationVariables,Types.UpdateBlackoutMutation['item']> = async ({state, effects}, payload) => {
    state.blackouts.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateBlackout(payload);
        return item;
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteBlackoutMutationVariables,Types.DeleteBlackoutMutation['item']> = async ({state, effects}, payload) => {
    state.blackouts.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteBlackout(payload);
        return item;
    } catch (e: any) {
        state.blackouts.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetBlackoutsMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetBlackoutsMetaSubscription, Types.SyncGetBlackoutsMetaSubscriptionVariables>(
        'syncGetBlackoutsMeta', 
        subscriptions.SyncGetBlackoutsMeta, 
        { where }, 
        actions.blackouts.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetBlackoutsMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBlackoutsMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.blackouts.errors = [ ...state.blackouts.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.blackouts.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetBlackoutsSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.blackouts.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.blackouts.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetBlackoutsSubscription, Types.SyncGetBlackoutsSubscriptionVariables>(
        'syncGetBlackouts', 
        subscriptions.SyncGetBlackouts, 
        {...originalPayload, ...payload}, 
        actions.blackouts.onItemsData,
        () => { state.blackouts.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetBlackouts');
    state.blackouts.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBlackoutsSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.blackouts.errors = [ ...state.blackouts.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.blackouts.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetBlackoutsSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.blackouts.syncGetItems(finalPayload);
    actions.blackouts.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.blackouts.unsubItemsData();
    actions.blackouts.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetBlackoutSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.blackouts.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetBlackoutSubscription, Types.SyncGetBlackoutSubscriptionVariables>(
        'syncGetBlackout', 
        subscriptions.SyncGetBlackout, 
        payload, 
        actions.blackouts.onItemData,
        () => { state.blackouts.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetBlackout');
    state.blackouts.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBlackoutSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.blackouts.errors = [ ...state.blackouts.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.blackouts.currentItemId = payload.data.item.id;
        state.blackouts.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetBlackoutSubscriptionVariables> = async ({actions}, payload) => {
    actions.blackouts.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.blackouts.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};